import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"; // To get deviceId from URL
import { getAllEvents } from "../../../../redux/events/eventActions";
// Assuming you might want to fetch the specific device details if not already loaded
// import { getDeviceByIdStartAsync } from "../../../../redux/assets/assetsActions"; 

// Shadcn UI components
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Gauge, Waypoints, Fuel, ArrowLeft } from 'lucide-react'; // Added ArrowLeft for back button
import { Button } from "@/components/ui/button"; // For back button
import { useNavigate } from "react-router-dom"; // For back button

import EventsLogTableComponent from "./EventsLogTable.component"; // Make sure path is correct

const columns = [
  { header: 'DATE', field: 'createdAt' },
  { header: 'HOUR', field: 'createdAt' },
  { header: 'TYPE', field: 'type' },
];

export default function DeviceFullDetailsPage() {
  const dispatch = useDispatch();
  const { deviceId } = useParams(); // Get deviceId from URL
  const navigate = useNavigate();

  const user = useSelector((state) => state.user.currentUser);
  // Select the specific device from the Redux store, or fetch it.
  // This assumes 'devices' is an array in your assets state.
  const device = useSelector((state) => 
    state.assets.devices.find(d => d._id === deviceId) || 
    state.assets.devices.find(d => d.deviceId === deviceId) // Fallback to deviceId field if _id is not matching
  ); 
  const events = useSelector((state) => state.events.events); // Assuming events are fetched for the specific device
  // Add loading states from redux if available, e.g., state.assets.isLoading, state.events.isLoading

  useEffect(() => {
    // Optional: If device data is not found in store, dispatch an action to fetch it
    // if (!device && deviceId && user) {
    //   dispatch(getDeviceByIdStartAsync(deviceId, user.token));
    // }

    // Fetch events for this specific device
    if (deviceId && user?.user?._id && user?.token) {
      dispatch(
        getAllEvents(user.user._id, deviceId, user.token)
      );
    }
  }, [dispatch, deviceId, user, device]); // device dependency to re-evaluate if device data changes

  if (!device) {
    // Handle case where device data is not yet loaded or not found
    // You could show a spinner from Shadcn UI here, e.g., <Loader2 className="animate-spin" />
    return <div className="p-4 text-center">Loading device details or device not found...</div>;
  }

  const avgSpeed = device.stats ? Math.round(device.stats.averageSpeed) : 0;
  const totalKm = device.stats ? Math.round(device.stats.totalKm) : 0;
  const remainingFuel = device.stats?.fuelLevel || 60; // Using optional chaining and a fallback

  return (
    <div className="p-4 md:p-6 flex flex-col gap-4 h-full overflow-y-auto">
        <Button 
            variant="outline" 
            onClick={() => navigate(-1)} 
            className="mb-4 w-fit"
        >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Device List
        </Button>

      {/* Flex container for side-by-side layout on larger screens */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Vehicle Statistics Section */}
        <Card className="w-full lg:w-1/3">
          <CardHeader>
            <CardTitle className="text-xl">Vehicle Statistics</CardTitle>
            <p className="text-sm text-muted-foreground">Device ID: {device.deviceId}</p>
          </CardHeader>
          <CardContent className="p-4">
            <Carousel className="w-full max-w-xs mx-auto">
              <CarouselContent>
                <CarouselItem>
                  <div className="p-1 flex flex-col items-center text-center">
                    <Gauge className="w-10 h-10 text-blue-600 mb-2" />
                    <p className="text-xs text-gray-500">Average Speed</p>
                    <p className="text-2xl font-semibold">{avgSpeed} <span className="text-sm">km/h</span></p>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-1 flex flex-col items-center text-center">
                    <Waypoints className="w-10 h-10 text-green-600 mb-2" />
                    <p className="text-xs text-gray-500">Total Distance</p>
                    <p className="text-2xl font-semibold">{totalKm} <span className="text-sm">km</span></p>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-1 flex flex-col items-center text-center">
                    <Fuel className="w-10 h-10 text-orange-500 mb-2" />
                    <p className="text-xs text-gray-500">Remaining Fuel</p>
                    <Progress value={remainingFuel} className="w-3/4 mt-2 h-3" />
                    <p className="text-lg font-semibold mt-1">{remainingFuel}%</p>
                  </div>
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious className="ml-2" />
              <CarouselNext className="mr-2"/>
            </Carousel>
          </CardContent>
        </Card>

        {/* Event Log Section */}
        {/* flex-grow to take remaining space if wrapped in a flex container with fixed height */}
        <Card className="w-full lg:w-2/3 flex flex-col max-h-[70vh] lg:max-h-none"> 
          <CardHeader>
            <CardTitle className="text-xl">Event Log</CardTitle>
          </CardHeader>
          <CardContent className="flex-grow p-1 overflow-hidden">
            <div className="h-full w-full overflow-auto"> {/* Inner scroll for table */}
              <EventsLogTableComponent events={events} columns={columns} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 